import React from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { FaBox, FaCalendarAlt, FaEdit, FaPlus } from 'react-icons/fa';
import { GET_RESTOCK_SHIPMENTS } from './RestockShipments.queries';
import Button from '../common/Button';
import { Screen } from '../Screen';
import Big from 'big.js';
import dayjs from 'dayjs';
import { GetRestockShipmentsQuery, GetRestockShipmentsQueryVariables } from '../../../codegen/graphql';

const RestockShipmentsList: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<
    GetRestockShipmentsQuery,
    GetRestockShipmentsQueryVariables
  >(GET_RESTOCK_SHIPMENTS, {
    fetchPolicy: 'cache-and-network',
  });

  if (error) {
    return (
      <Screen>
        <div className="p-8 text-red-600">Error loading restock shipments: {error.message}</div>
      </Screen>
    );
  }

  const shipments = [...(data?.restockShipments || [])].sort((a, b) => {
    const aCreatedAt = (a as any).createdAt as string | null | undefined;
    const bCreatedAt = (b as any).createdAt as string | null | undefined;
    const aTime = aCreatedAt ? new Date(aCreatedAt).getTime() : 0;
    const bTime = bCreatedAt ? new Date(bCreatedAt).getTime() : 0;
    if (bTime !== aTime) return bTime - aTime;
    return b.id.localeCompare(a.id);
  });

  const { totalBlockedInventory, totalSoldInventory, totalInventory } = shipments.reduce((acc, shipment) => {
    acc.totalBlockedInventory.quantity += shipment.inventory.total.quantity - shipment.inventory.sold.quantity;
    acc.totalBlockedInventory.amount += shipment.inventory.total.amount - shipment.inventory.sold.amount;
    acc.totalSoldInventory.quantity += shipment.inventory.sold.quantity;
    acc.totalSoldInventory.amount += shipment.inventory.sold.amount;
    acc.totalInventory.quantity += shipment.inventory.total.quantity;
    acc.totalInventory.amount += shipment.inventory.total.amount;
    return acc;
  }, { totalBlockedInventory: {
    quantity: 0,
    amount: 0,
  }, totalSoldInventory: {
    quantity: 0,
    amount: 0,
  }, totalInventory: {
    quantity: 0,
    amount: 0,
  } });

  const averageSoldPercent = totalInventory.amount
    ? new Big(totalSoldInventory.amount).div(totalInventory.amount).mul(100).round(2).toNumber()
    : 0;
  const roundedSoldPercent = Math.round(averageSoldPercent);
  const formatAmount = (value: number) => Math.round(value).toLocaleString();
  const formatCount = (value: number) => Math.round(value).toLocaleString();

  return (
    <Screen>
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-5">
          <h1 className="text-3xl font-bold text-gray-800">Restock Shipments</h1>
          <Button
            preset="primary"
            onClick={() => navigate('/restock-shipments/new')}
          >
            <FaPlus />
            New Shipment
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Shipments</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{formatCount(shipments.length)}</p>
            <p className="text-lg text-gray-500 mt-1">Newest first by date</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Total Inventory</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{formatAmount(totalInventory.amount)} EUR</p>
            <p className="text-lg text-gray-600 mt-1">{formatCount(totalInventory.quantity)} units</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Sold Inventory</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{formatAmount(totalSoldInventory.amount)} EUR</p>
            <p className="text-lg text-gray-600 mt-1">{formatCount(totalSoldInventory.quantity)} units</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Blocked Inventory</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{formatAmount(totalBlockedInventory.amount)} EUR</p>
            <p className="text-lg text-gray-600 mt-1">{formatCount(totalBlockedInventory.quantity)} units</p>
          </div>
          <div className="bg-yellow-300 rounded-xl border border-yellow-400 shadow-sm p-4">
            <p className="text-xs uppercase tracking-wide text-black">Sold Rate</p>
            <p className="text-5xl font-bold text-black leading-none mt-1">{roundedSoldPercent}%</p>
          </div>
        </div>

        {loading && shipments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Loading shipments...</div>
        ) : shipments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FaBox className="mx-auto text-6xl text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No Restock Shipments</h2>
            <p className="text-gray-500 mb-4">Create your first restock shipment to get started.</p>
            <Button
              onClick={() => navigate('/restock-shipments/new')}
              preset="primary"
            >
              <FaPlus />
              Create Shipment
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {shipments.map((shipment: any) => (
              <div
                key={shipment.id}
                className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/restock-shipments/${shipment.id}/edit`)}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <FaBox className="text-2xl text-yellow-500 mr-3 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-800 text-lg">
                          {shipment.title || shipment.shipName || 'Unnamed Shipment'}
                        </h3>
                        <p className="text-xs text-gray-500">ID: {shipment.id.slice(0, 8)}...</p>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <FaCalendarAlt />
                          {shipment.createdAt
                            ? dayjs(shipment.createdAt).format('MMM DD, YYYY HH:mm')
                            : 'Date unavailable'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {shipment.deliveryAddress ? (
                    <div className="mb-3 text-sm text-gray-600">
                      <span className="font-medium">Address:</span> {shipment.deliveryAddress.slice(0, 50)}
                      {shipment.deliveryAddress.length > 50 && '...'}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="rounded-lg bg-gray-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Total Weight</p>
                      <p className="text-xl font-bold text-gray-900">{formatAmount(shipment.totalWeight)} kg</p>
                    </div>
                    <div className="rounded-lg bg-gray-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Freight Cost</p>
                      <p className="text-xl font-bold text-gray-900">
                        {shipment.freightCost
                          ? `${formatAmount(shipment.freightCost)} ${shipment.freightCostCurrency || ''}`
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {shipment.inventory && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-lg font-bold text-gray-900">{formatAmount(shipment.inventory.total.amount)} {shipment.inventory.total.amountCurrency}</p>
                        <p className="text-xs text-gray-500">
                          {formatCount(shipment.inventory.total.quantity)} units
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Sold</p>
                        <p className="text-lg font-bold text-gray-900">{formatAmount(shipment.inventory.sold.amount)} {shipment.inventory.sold.amountCurrency}</p>
                        <p className="text-xs text-gray-500">
                          {formatCount(shipment.inventory.sold.quantity)} units
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Blocked</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatAmount(
                            new Big(shipment.inventory.total.amount)
                              .minus(shipment.inventory.sold.amount)
                              .toNumber()
                          )} {shipment.inventory.total.amountCurrency}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatCount(Math.max(
                            shipment.inventory.total.quantity - shipment.inventory.sold.quantity,
                            0
                          ))} units
                        </p>
                        <p className="text-[11px] text-gray-600 font-semibold">
                          Sold: {shipment.inventory.total.amount
                            ? Math.round(new Big(shipment.inventory.sold.amount)
                              .div(shipment.inventory.total.amount)
                              .mul(100)
                              .toNumber())
                            : 0}% 
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                    <Button
                      size="sm"
                      preset="white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/restock-shipments/${shipment.id}/edit`);
                      }}
                    >
                      <FaEdit />
                      Edit Products
                    </Button>
                    <Button
                      size="sm"
                      preset="white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/restock-shipments/${shipment.id}/boxes`);
                      }}
                    >
                      <FaBox />
                      Manage Boxes
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
};

export default RestockShipmentsList;

